import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { useAuth, PLP_LOGO, API } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Home,
  MapPin,
  Wrench,
  Radio,
  BookOpen,
  Users,
  User,
  Menu,
  LogOut,
  ChevronDown,
  Shield,
  Newspaper,
  Award,
  Building2,
  Settings,
  Car,
  Phone,
  Mic,
  MessageSquare,
  Vote,
  Image as ImageIcon
} from 'lucide-react';
import ChatBot from './ChatBot';

const navItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/safety-map', label: 'Safer Together', icon: Shield },
  { path: '/fix-it-forward', label: 'Fix It Forward', icon: Wrench },
  { path: '/engage', label: 'Robyn Responds', icon: Radio },
  { path: '/townhall', label: 'Town Hall', icon: Mic },
  { path: '/carpool', label: 'Killarney Rides', icon: Car },
  { path: '/news', label: 'Community News', icon: Newspaper },
  { path: '/directory', label: 'Directory', icon: Building2 },
  { path: '/get-involved', label: 'Get Involved', icon: Users },
];

const Layout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content`);
        if (response.data && Object.keys(response.data).length > 0) {
          setSiteContent(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch site content:', error);
      }
    };
    fetchSiteContent();
  }, []);

  // Get emergency contacts with fallbacks
  const emergencyContent = siteContent?.emergency || {};
  const showEmergencyBanner = emergencyContent.show_banner !== false;
  const emergencyContacts = emergencyContent.contacts?.length > 0 
    ? emergencyContent.contacts 
    : [
        { label: 'Emergency', number: '919' },
        { label: 'Airport Police', number: '242-377-8391' },
        { label: 'Cable Beach Police', number: '242-327-8800' }
      ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Emergency Contacts Banner */}
      {showEmergencyBanner && (
        <div className="bg-red-600 text-white text-sm py-1.5 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 md:gap-8 flex-wrap text-xs md:text-sm">
            {emergencyContacts.map((contact, idx) => (
              <span key={`emergency-${contact.label}-${idx}`} className="flex items-center gap-1.5">
                {idx === 0 && <Phone className="w-3.5 h-3.5" />}
                <span className={idx === 0 ? "font-medium" : ""}>
                  <span className="hidden md:inline">{contact.label}:</span>
                  <span className="md:hidden">{contact.label.split(' ')[0]}:</span>
                </span> {contact.number}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
              <img src={PLP_LOGO} alt="PLP Logo" className="h-10 w-auto object-contain" />
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-plp-blue text-sm lg:text-base">Killarney Connect</span>
                <p className="text-xs text-slate-500 hidden lg:block">Progress in Action</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="flex items-center gap-0.5 lg:gap-1 ml-4">
              {navItems.slice(0, 4).map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-2 lg:px-3 py-1.5 rounded-full text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-plp-blue text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs lg:text-sm font-medium px-2 lg:px-3">
                    More <ChevronDown className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {navItems.slice(4).map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center gap-2">
                        <item.icon className="w-4 h-4" /> {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/polls" className="flex items-center gap-2">
                      <Vote className="w-4 h-4" /> Community Polls
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/gallery" className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" /> Photo Gallery
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/spotlights" className="flex items-center gap-2">
                      <Award className="w-4 h-4" /> Resident Spotlights
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/resources" className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> Resources
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/about" className="flex items-center gap-2">
                      <User className="w-4 h-4" /> About Robyn
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Auth Section */}
            <div className="flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 bg-plp-gold rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-plp-blue" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{user.name}</span>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {user.is_admin && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2">
                            <Settings className="w-4 h-4" /> Admin Portal
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2">
                        <User className="w-4 h-4" /> My Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-slate-600">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/auth">
                  <Button className="bg-plp-blue hover:bg-plp-blue-light text-white rounded-full px-6" data-testid="signin-btn">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="mobile-header-logo">
            <img src={PLP_LOGO} alt="PLP Logo" className="h-10 w-auto object-contain" />
            <span className="font-heading font-bold text-plp-blue text-sm">Killarney Connect</span>
          </Link>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-6 mt-6">
                {user && (
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <div className="w-10 h-10 bg-plp-gold rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-plp-blue" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      {user.is_admin && (
                        <Link to="/admin" className="text-xs text-plp-blue font-medium" onClick={() => setMobileMenuOpen(false)}>
                          Admin Portal
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                <nav className="flex flex-col gap-1">
                  {[...navItems, 
                    { path: '/spotlights', label: 'Spotlights', icon: Award },
                    { path: '/resources', label: 'Resources', icon: BookOpen },
                    { path: '/about', label: 'About Robyn', icon: User }
                  ].map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-plp-blue text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>

                <div className="pt-4 border-t">
                  {user ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full justify-start gap-3 text-red-600 border-red-200"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </Button>
                  ) : (
                    <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-plp-blue hover:bg-plp-blue-light text-white" data-testid="mobile-signin-btn">
                        Sign In
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="mobile-nav-padding">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 px-2 py-2">
        <div className="flex items-center justify-around">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all ${
                  isActive ? 'text-plp-blue' : 'text-slate-400'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-plp-blue' : ''}`} />
                <span className="text-[10px] font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Chatbot */}
      <ChatBot />
    </div>
  );
};

export default Layout;
